import { cn } from '@/lib/utils';
import type { ApprovalStatus, Bucket } from '@/lib/types';

export interface StatusBadgeProps {
  status: ApprovalStatus | string;
  className?: string;
}

const LIFECYCLE_STYLES: Record<string, string> = {
  pending: 'bg-warning-500/10 text-warning-500 border-warning-500/30',
  approved: 'bg-secondary-500/10 text-secondary-600 border-secondary-500/30',
  posted: 'bg-primary-500/10 text-primary-600 border-primary-500/30',
  duplicate: 'bg-warning-500/10 text-warning-500 border-warning-500/30',
  rejected: 'bg-error-500/10 text-error-500 border-error-500/30',
  expired: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  cancelled: 'bg-neutral-100 text-neutral-600 border-neutral-200',
};

const LIFECYCLE_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  posted: 'Posted',
  duplicate: 'Duplicate',
  rejected: 'Rejected',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const BUCKET_STYLES: Record<Bucket, string> = {
  POSTED: 'bg-primary-500/10 text-primary-600 border-primary-500/30',
  POSTED_SERVICE: 'bg-primary-500/10 text-primary-600 border-primary-500/30',
  DUPLICATE: 'bg-warning-500/10 text-warning-500 border-warning-500/30',
  REJECTED: 'bg-error-500/10 text-error-500 border-error-500/30',
  SUPPLIER_AMBIGUOUS: 'bg-orange-100 text-orange-900 border-orange-200',
  SUPPLIER_NOT_FOUND: 'bg-orange-100 text-orange-900 border-orange-200',
  MULTIPLE_GRNS: 'bg-orange-100 text-orange-900 border-orange-200',
  GRN_UNAPPROVED: 'bg-amber-100 text-amber-900 border-amber-200',
  GRN_NOT_FOUND: 'bg-orange-100 text-orange-900 border-orange-200',
  AMOUNT_MISMATCH: 'bg-orange-100 text-orange-900 border-orange-200',
  GRN_DETAIL_SPLIT_UNSUPPORTED: 'bg-orange-100 text-orange-900 border-orange-200',
  TAX_MISMATCH_REVIEW: 'bg-orange-100 text-orange-900 border-orange-200',
  DATE_REVIEW: 'bg-orange-100 text-orange-900 border-orange-200',
  SERVICE_PREP: 'bg-purple-100 text-purple-900 border-purple-200',
  POST_FAILED: 'bg-error-500/10 text-error-500 border-error-500/30',
  PARSE_ERROR: 'bg-error-500/10 text-error-500 border-error-500/30',
  EXP_REB_REVIEW: 'bg-orange-100 text-orange-900 border-orange-200',
  MIXED_PI_BOOK_CONTEXT: 'bg-orange-100 text-orange-900 border-orange-200',
  UNKNOWN: 'bg-neutral-100 text-neutral-700 border-neutral-200',
};

const BUCKET_LABELS: Record<Bucket, string> = {
  POSTED: 'Posted',
  POSTED_SERVICE: 'Posted (Service)',
  DUPLICATE: 'Duplicate',
  REJECTED: 'Rejected',
  SUPPLIER_AMBIGUOUS: 'Supplier ambiguous',
  SUPPLIER_NOT_FOUND: 'Supplier not found',
  MULTIPLE_GRNS: 'Multiple GRNs',
  GRN_UNAPPROVED: 'GRN awaiting approval',
  GRN_NOT_FOUND: 'No GRN found',
  AMOUNT_MISMATCH: 'Amount mismatch',
  GRN_DETAIL_SPLIT_UNSUPPORTED: 'GRN split unsupported',
  TAX_MISMATCH_REVIEW: 'Tax review',
  DATE_REVIEW: 'Invoice date review',
  SERVICE_PREP: 'Service prep',
  POST_FAILED: 'Post failed',
  PARSE_ERROR: 'Could not read invoice',
  EXP_REB_REVIEW: 'Exp/Reb review',
  MIXED_PI_BOOK_CONTEXT: 'Goods posting context issue',
  UNKNOWN: 'Unknown',
};

const BADGE_BASE =
  'inline-flex items-center rounded-full border px-8 py-2 text-xs font-semibold uppercase tracking-wide';

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const key = (status || '').toString().toLowerCase();
  const style = LIFECYCLE_STYLES[key] ?? 'bg-neutral-100 text-neutral-700 border-neutral-200';
  const label = LIFECYCLE_LABELS[key] ?? status;
  return <span className={cn(BADGE_BASE, style, className)}>{label}</span>;
}

export interface BucketBadgeProps {
  bucket: Bucket | null | undefined;
  className?: string;
}

export function BucketBadge({ bucket, className }: BucketBadgeProps) {
  const key: Bucket = (bucket ?? 'UNKNOWN') as Bucket;
  const style = BUCKET_STYLES[key] ?? BUCKET_STYLES.UNKNOWN;
  const label = BUCKET_LABELS[key] ?? key;
  return <span className={cn(BADGE_BASE, style, className)}>{label}</span>;
}
